
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "./Analytics.css";
import KeyMetrics from "./KeyMetrics";
import axios from "axios";
import AnalyticsSidebar from './analyticsSidebar'; // Sidebar component


const Analytics = () => {
  const navigate = useNavigate(); // Hook for navigation
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const initialSearchTerm = queryParams.get("search") || "";

  const [selectedOption, setSelectedOption] = useState("key-metrics");
  const [searchTerm, setSearchTerm] = useState(localStorage.getItem("searchTerm") || "");
  const storedFromDate = localStorage.getItem("fromDate");
const storedToDate = localStorage.getItem("toDate");

const [fromDate, setFromDate] = useState(
  storedFromDate && !isNaN(Date.parse(storedFromDate)) ? new Date(storedFromDate) : null
);
const [toDate, setToDate] = useState(
  storedToDate && !isNaN(Date.parse(storedToDate)) ? new Date(storedToDate) : null
);

  const [selectedMediaTypes, setSelectedMediaTypes] = useState(JSON.parse(localStorage.getItem("selectedMediaTypes")) || []);
  const [selectedTimeRange, setSelectedTimeRange] = useState(localStorage.getItem("selectedTimeRange") || null);
  

  const [selectedSentiment, setSelectedSentiment] = useState(null);
  
  const [apiData, setApiData] = useState(null); // Store API response
  const [mediaDropdownOpen, setMediaDropdownOpen] = useState(false);
  const mediaDropdownRef = useRef(null); // Ref for dropdown container

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (mediaDropdownRef.current && !mediaDropdownRef.current.contains(event.target)) {
        setMediaDropdownOpen(false);
      }
    }
  
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);
  
  const toggleMediaDropdown = (event) => {
    event.stopPropagation(); // Prevent click from triggering outside close
    setMediaDropdownOpen((prev) => !prev);
  };
  


  useEffect(() => {
    if (searchTerm) localStorage.setItem("searchTerm", searchTerm);
    else localStorage.removeItem("searchTerm");
  
    if (fromDate) localStorage.setItem("fromDate", fromDate.toISOString());
    else localStorage.removeItem("fromDate");
  
    if (toDate) localStorage.setItem("toDate", toDate.toISOString());
    else localStorage.removeItem("toDate");
  
    if (selectedMediaTypes.length > 0)
      localStorage.setItem("selectedMediaTypes", JSON.stringify(selectedMediaTypes));
    else localStorage.removeItem("selectedMediaTypes");
  
    if (selectedTimeRange) localStorage.setItem("selectedTimeRange", selectedTimeRange);
    else localStorage.removeItem("selectedTimeRange");
  }, [searchTerm, fromDate, toDate, selectedMediaTypes, selectedTimeRange]);
  
  

  const inputRef = useRef(null);

  const handleDateChange = (type, date) => {
    if (type === "from") setFromDate(date);
    else setToDate(date);
  };

  const handleResetFilters = () => {
    setFromDate(null); // ✅ Make dates empty instead of setting to today
    setToDate(null);
    setSelectedSentiment(null);
    setSelectedMediaTypes([]); // ✅ Clear selected media platforms
    setSelectedTimeRange(null); // ✅ Remove any selected time range
    setSearchTerm(""); // ✅ Clear the search term
  };
  

  

  const handleMediaTypeChange = (type) => {
    setSelectedMediaTypes((prevSelected) =>
      prevSelected.includes(type)
        ? prevSelected.filter((item) => item !== type)
        : [...prevSelected, type]
    );
  };

  const handleApplyFilters = async () => {
    if (!searchTerm.trim()) {
      alert("Please enter a search term.");
      return;
    }
  
    if (selectedMediaTypes.length === 0) {
      alert("Please select at least one media platform.");
      return;
    }
  
    try {
      // Prepare request payload
      const requestData = {
        searchTerm,
        selectedMediaTypes,
      };
  
      // Add date filters only if they are selected
      if (fromDate) {
        requestData.from_date = fromDate.toISOString().split("T")[0];
      }
      if (toDate) {
        requestData.to_date = toDate.toISOString().split("T")[0];
      }
  
      // Send request
      const response = await axios.post(
        "api/analytics-data/",
        requestData,
        {
          headers: { "Content-Type": "application/json" },
        }
      );
  
      console.log("API Response:", response.data);
      setApiData(response.data); // Store API data in state
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };
  

  const handleTimeRangeChange = (range) => {
    setSelectedTimeRange(range);
    const currentDate = new Date();
    let newFromDate;

    switch (range) {
      case "1D":
        newFromDate = new Date(currentDate.setDate(currentDate.getDate() - 1));
        break;
      case "7D":
        newFromDate = new Date(currentDate.setDate(currentDate.getDate() - 7));
        break;
      case "1M":
        newFromDate = new Date(
          currentDate.setMonth(currentDate.getMonth() - 1)
        );
        break;
      case "3M":
        newFromDate = new Date(
          currentDate.setMonth(currentDate.getMonth() - 3)
        );
        break;
      default:
        newFromDate = null;
    }

    setFromDate(newFromDate);
    setToDate(newFromDate ? new Date() : null); // Set "To Date" only if a range is selected
  };

  // Function to render the selected content
  const renderContent = () => {
    const commonProps = {
      searchTerm,
      fromDate,
      toDate,
      selectedSentiment,
      selectedMediaTypes,
      apiData, // ✅ Pass API Data to KeyMetrics.js
    };
  
    switch (selectedOption) {
      case "key-metrics":
        return <KeyMetrics {...commonProps} />;
      default:
        return <KeyMetrics {...commonProps} />;
    }
  };
  

  return (
    <div className="analytics-grid">
      <div className="main-content">
        <div className="A-filter-bar">
          <div className="input-container">
            <input
              type="text"
              placeholder="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              ref={inputRef}
            />
          </div>
          <button id="applyFilters" onClick={handleApplyFilters}>
            Apply
          </button>
          <button id="resetFilters" onClick={handleResetFilters}>
            Reset Filters
          </button>
        </div>

        <div className="filter-bar">
          {/* Media Types Dropdown */}
          <div className="dropdown-wrapper-analytics" ref={mediaDropdownRef}>
            <button className="filter-button" onClick={toggleMediaDropdown}>
              Select Platform
            </button>
            {mediaDropdownOpen && (
              <div className="dropdown-content-analytics">
                {["Twitter", "Facebook", "Instagram", "TikTok"].map((platform) => (
                  <label key={platform}>
                    <input
                      type="checkbox"
                      checked={selectedMediaTypes.includes(platform)}
                      onChange={() => handleMediaTypeChange(platform)}
                    />
                    {platform}
                  </label>
                ))}
              </div>
            )}
          </div>

          {/* Time Range Buttons */}
          <div className="time-filters">
            {["1D", "7D", "1M"].map((range) => (
              <button
                key={range}
                className={`time-button ${selectedTimeRange === range ? "active" : ""}`}
                onClick={() => handleTimeRangeChange(range)}
              >
                {range}
              </button>
            ))}
          </div>

          {/* Date Range Pickers */}
          <div className="date-range">
            <label>From: </label>
            <DatePicker
              selected={fromDate}
              onChange={(date) => {
                setFromDate(date);
                localStorage.setItem("fromDate", date ? date.toISOString() : "");
              }}
              dateFormat="MM/dd/yyyy"
              className="date-picker"
              isClearable
            />
            <label>To: </label>
            <DatePicker
              selected={toDate}
              onChange={(date) => {
                setToDate(date);
                localStorage.setItem("toDate", date ? date.toISOString() : "");
              }}
              dateFormat="MM/dd/yyyy"
              className="date-picker"
              isClearable
            />
          </div>
        </div>

        {/* Render selected content */}
        <div className="key-metrics-container">{renderContent()}</div>
      </div>
    </div>
  );

};

export default Analytics;
